import React, { useState, useEffect, useRef } from 'react';
import useGenerateText from '../../hooks/useGenerateText';
import DraftEditor, { DraftEditorHandle } from './DraftEditor';

interface QuestionVariations {
  singular: string;
  plural: string;
}

interface AnswerVariations {
  id: string;
  text: QuestionVariations;
  isCorrect: boolean;
  isFreeText: boolean;
  image?: string;
}

interface QuestionMultipleContent {
  questionText: string | QuestionVariations;
  questionImage?: string;
  answers: any[];
  allowMultiple: boolean;
}

interface QuestionMultipleEditorProps {
  content: QuestionMultipleContent;
  onChange: (content: {
    questionText: QuestionVariations;
    questionImage?: string;
    answers: AnswerVariations[];
    allowMultiple: boolean;
  }) => void;
  testQuestionImage: string;
  onQuestionImageChange: (image: string) => void;
  allowMultipleAnswers: boolean;
  onAllowMultipleChange: (allow: boolean) => void;
  isEditable: boolean;
  onEditableChange: (editable: boolean) => void;
  showEditableToggle?: boolean;
}

type TabKey = keyof QuestionVariations;

const QuestionMultipleEditor: React.FC<QuestionMultipleEditorProps> = ({
  content,
  onChange,
  testQuestionImage,
  onQuestionImageChange,
  allowMultipleAnswers,
  onAllowMultipleChange,
  isEditable,
  onEditableChange,
  showEditableToggle = true
}) => {
  // Editor refs
  const questionEditorRef = useRef<DraftEditorHandle | null>(null);
  const answerEditorRef = useRef<DraftEditorHandle | null>(null);

  const [activeQuestionTab, setActiveQuestionTab] = useState<TabKey>('singular');
  const [editingAnswerIndex, setEditingAnswerIndex] = useState<number | null>(null);
  const [activeAnswerTab, setActiveAnswerTab] = useState<TabKey>('singular');
  const [isAddingNewAnswer, setIsAddingNewAnswer] = useState(false);
  const [newAnswerText, setNewAnswerText] = useState('');
  const [newAnswerImage, setNewAnswerImage] = useState('');
  const [newAnswerIsFreeText, setNewAnswerIsFreeText] = useState(false);
  const { generateText, isGenerating, error, clearError } = useGenerateText();

  // Utility function to fill empty variations with singular content.
  // Coerces non-string fields (legacy rows may have null/undefined plurals) to strings.
  const fillEmptyVariationsWithSingular = (variations: QuestionVariations): QuestionVariations => {
    const singular = typeof variations.singular === 'string' ? variations.singular : '';
    const plural = typeof variations.plural === 'string' ? variations.plural : '';

    if (singular.trim() !== '' && plural.trim() === '') {
      return { ...variations, singular, plural: singular };
    }

    return { ...variations, singular, plural };
  };

  // Initialize question variations from content prop
  const [questionVariations, setQuestionVariations] = useState<QuestionVariations>(() => {
    let initialVariations: QuestionVariations;

    if (typeof content.questionText === 'string') {
      // Legacy format - use string as singular
      initialVariations = {
        singular: content.questionText,
        plural: ''
      };
    } else if (content.questionText && typeof content.questionText === 'object') {
      const contentObj = content.questionText as any;
      // Check if it's the new format with singular/plural
      if ('singular' in contentObj) {
        initialVariations = {
          singular: contentObj.singular || '',
          plural: contentObj.plural || ''
        };
      }
      // Legacy format with original/tuSingular/etc
      else if ('original' in contentObj) {
        initialVariations = {
          singular: contentObj.original || contentObj.tuSingular || '',
          plural: contentObj.tuPlural || ''
        };
      }
      // Try to find text in various other legacy formats
      else {
        let textValue = '';

        // Check for nested content structures
        if (contentObj.content) {
          if (typeof contentObj.content === 'string') {
            textValue = contentObj.content;
          } else if (contentObj.content.text) {
            textValue = contentObj.content.text;
          } else if (contentObj.content.questionText) {
            textValue = contentObj.content.questionText;
          } else if (contentObj.content.content && typeof contentObj.content.content === 'string') {
            textValue = contentObj.content.content;
          }
        }

        // Check for other common field names
        if (!textValue) {
          textValue = contentObj.questionText || contentObj.text || contentObj.textContent ||
                      contentObj.value || contentObj.name || contentObj.title || '';
        }

        initialVariations = {
          singular: textValue,
          plural: ''
        };
      }
    } else {
      // Default empty state
      initialVariations = {
        singular: '',
        plural: ''
      };
    }

    // Auto-fill empty plural with singular content if applicable
    return fillEmptyVariationsWithSingular(initialVariations);
  });

  // Initialize answers from content prop
  const [answers, setAnswers] = useState<AnswerVariations[]>(() => {
    return content.answers.map((answer, index) => {
      let answerVariations: QuestionVariations;

      if (answer.text && typeof answer.text === 'object') {
        // Check for new format with singular/plural
        if ('singular' in answer.text) {
          answerVariations = {
            singular: answer.text.singular || '',
            plural: answer.text.plural || ''
          };
        }
        // Legacy format with original/tuSingular/etc
        else if ('original' in answer.text) {
          answerVariations = {
            singular: answer.text.original || answer.text.tuSingular || '',
            plural: answer.text.tuPlural || ''
          };
        }
        // Other legacy formats
        else {
          const textObj = answer.text as any;
          let answerText = '';

          // Check for nested content structures
          if (textObj.content) {
            if (typeof textObj.content === 'string') {
              answerText = textObj.content;
            } else if (textObj.content.text) {
              answerText = textObj.content.text;
            } else if (textObj.content.content && typeof textObj.content.content === 'string') {
              answerText = textObj.content.content;
            }
          }

          // Check for other common field names
          if (!answerText) {
            answerText = textObj.textContent || textObj.value || textObj.name || textObj.title || '';
          }

          answerVariations = {
            singular: answerText,
            plural: ''
          };
        }
      } else {
        // Legacy format - string directly
        const answerText = typeof answer.text === 'string' ? answer.text : '';
        answerVariations = {
          singular: answerText,
          plural: ''
        };
      }

      // Auto-fill empty answer variations with singular content if applicable
      const filledAnswerVariations = fillEmptyVariationsWithSingular(answerVariations);

      return {
        id: answer.id || `answer-${index}`,
        text: filledAnswerVariations,
        isCorrect: answer.isCorrect || false,
        isFreeText: answer.isFreeText || false,
        image: answer.image
      };
    });
  });

  // Update parent whenever data changes (only call onChange when data actually changes)
  const prevDataRef = useRef<any>(null);

  useEffect(() => {
    const currentData = {
      questionText: questionVariations,
      questionImage: testQuestionImage,
      answers: answers,
      allowMultiple: allowMultipleAnswers
    };

    // Only call onChange if data actually changed
    if (!prevDataRef.current ||
        JSON.stringify(prevDataRef.current.questionText) !== JSON.stringify(questionVariations) ||
        prevDataRef.current.questionImage !== testQuestionImage ||
        JSON.stringify(prevDataRef.current.answers) !== JSON.stringify(answers) ||
        prevDataRef.current.allowMultiple !== allowMultipleAnswers) {
      onChange(currentData);
      prevDataRef.current = { ...currentData };
    }
  }, [questionVariations, answers, testQuestionImage, allowMultipleAnswers, onChange]);

  const tabs = [
    { key: 'singular' as TabKey, label: 'Singular' },
    { key: 'plural' as TabKey, label: 'Plural' }
  ];

  // Sync question editor content when tab changes
  const prevQuestionTab = useRef(activeQuestionTab);
  useEffect(() => {
    if (prevQuestionTab.current !== activeQuestionTab && questionEditorRef.current) {
      questionEditorRef.current.setHTML(questionVariations[activeQuestionTab]);
      prevQuestionTab.current = activeQuestionTab;
    }
  }, [activeQuestionTab, questionVariations]);

  // Sync answer editor content when tab or editing index changes
  const prevAnswerTab = useRef(activeAnswerTab);
  const prevEditingIndex = useRef(editingAnswerIndex);
  useEffect(() => {
    if (answerEditorRef.current && (isAddingNewAnswer || editingAnswerIndex !== null)) {
      // Tab changed or started editing a different answer
      if (prevAnswerTab.current !== activeAnswerTab || prevEditingIndex.current !== editingAnswerIndex) {
        answerEditorRef.current.setHTML(newAnswerText);
        prevAnswerTab.current = activeAnswerTab;
        prevEditingIndex.current = editingAnswerIndex;
      }
    }
  }, [activeAnswerTab, editingAnswerIndex, newAnswerText, isAddingNewAnswer]);

  const handleQuestionContentChange = (value: string) => {
    setQuestionVariations(prev => {
      const updated = {
        ...prev,
        [activeQuestionTab]: value
      };

      // If we're editing the singular tab and plural is empty, auto-fill it
      if (activeQuestionTab === 'singular') {
        return fillEmptyVariationsWithSingular(updated);
      }

      return updated;
    });
  };

  const handleQuestionAutoGenerate = async () => {
    if (!questionVariations.singular || questionVariations.singular.trim() === '') {
      alert('Por favor, ingresa primero el texto singular antes de autogenerar el plural.');
      return;
    }

    clearError();

    try {
      // Generate plural from singular using 'tuPlural' type
      const generatedText = await generateText(questionVariations.singular, 'tuPlural');

      setQuestionVariations(prev => ({
        ...prev,
        plural: generatedText
      }));

    } catch (error) {
      console.error('Error generating text:', error);
    }
  };

  const startAddingAnswer = () => {
    setIsAddingNewAnswer(true);
    setEditingAnswerIndex(null);
    setActiveAnswerTab('singular');
    setNewAnswerText('');
    setNewAnswerImage('');
    setNewAnswerIsFreeText(false);
    clearError();
  };

  const startEditingAnswer = (index: number) => {
    setEditingAnswerIndex(index);
    setIsAddingNewAnswer(false);
    setActiveAnswerTab('singular');
    setNewAnswerText(answers[index].text.singular);
    setNewAnswerImage(answers[index].image || '');
    setNewAnswerIsFreeText(answers[index].isFreeText);
    clearError();
  };

  const saveAnswer = () => {
    if (!newAnswerText.trim()) return;

    if (isAddingNewAnswer) {
      const answerVariations: QuestionVariations = {
        singular: activeAnswerTab === 'singular' ? newAnswerText : '',
        plural: activeAnswerTab === 'plural' ? newAnswerText : ''
      };

      // Auto-fill empty variations if applicable
      const filledVariations = fillEmptyVariationsWithSingular(answerVariations);

      const newAnswer: AnswerVariations = {
        id: `answer-${Date.now()}`,
        text: filledVariations,
        isCorrect: false,
        isFreeText: newAnswerIsFreeText,
        image: newAnswerImage
      };
      setAnswers(prev => [...prev, newAnswer]);
    } else if (editingAnswerIndex !== null) {
      setAnswers(prev => {
        const updated = [...prev];
        const updatedText = {
          ...updated[editingAnswerIndex].text,
          [activeAnswerTab]: newAnswerText
        };

        // If we're editing the singular tab and plural is empty, auto-fill it
        const finalText = activeAnswerTab === 'singular' ?
          fillEmptyVariationsWithSingular(updatedText) : updatedText;

        updated[editingAnswerIndex] = {
          ...updated[editingAnswerIndex],
          text: finalText,
          isFreeText: newAnswerIsFreeText,
          image: newAnswerImage
        };
        return updated;
      });
    }

    // Reset state
    cancelAnswerEdit();
  };

  const cancelAnswerEdit = () => {
    setEditingAnswerIndex(null);
    setIsAddingNewAnswer(false);
    setNewAnswerText('');
    setNewAnswerImage('');
    setNewAnswerIsFreeText(false);
    setActiveAnswerTab('singular');
    clearError();
  };

  const deleteAnswer = (index: number) => {
    setAnswers(prev => prev.filter((_, i) => i !== index));
    if (editingAnswerIndex === index) {
      cancelAnswerEdit();
    }
  };

  const loadAnswerVariationText = (variation: TabKey) => {
    if (editingAnswerIndex !== null) {
      setNewAnswerText(answers[editingAnswerIndex].text[variation] || '');
    } else {
      setNewAnswerText('');
    }
    setActiveAnswerTab(variation);
  };

  const handleAnswerAutoGenerate = async () => {
    if (editingAnswerIndex === null || activeAnswerTab === 'singular') return;

    const answer = answers[editingAnswerIndex];
    if (!answer.text.singular || answer.text.singular.trim() === '') {
      alert('Esta respuesta necesita tener texto en la pestaña "Singular" para poder autogenerar el plural.');
      return;
    }

    clearError();

    try {
      // Generate plural from singular using 'tuPlural' type
      const generatedText = await generateText(answer.text.singular, 'tuPlural');

      setNewAnswerText(generatedText);

    } catch (error) {
      console.error('Error generating text:', error);
    }
  };

  const handleQuestionImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        onQuestionImageChange(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeQuestionImage = () => {
    onQuestionImageChange('');
  };

  const handleAnswerImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setNewAnswerImage(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAnswerImage = () => {
    setNewAnswerImage('');
  };

  return (
    <div className="mt-4 pt-2">
      {/* Question Text Section */}
      <div className="mb-4">
        <label className="form-label">Texto de la pregunta</label>
        <small className="text-muted d-block mb-3">
          Configura el texto de la pregunta en singular y plural.
        </small>

        {/* Question Tab Navigation */}
        <ul className="nav nav-tabs mb-0">
          {tabs.map(tab => (
            <li key={tab.key} className="nav-item">
              <button
                className={`nav-link ${activeQuestionTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveQuestionTab(tab.key)}
                type="button"
                style={{ color: '#000' }}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Question Editor */}
        <DraftEditor
          ref={questionEditorRef}
          defaultValue={questionVariations[activeQuestionTab]}
          onChange={(html) => handleQuestionContentChange(html)}
          placeholder={`Escribe el texto de la pregunta para ${tabs.find(t => t.key === activeQuestionTab)?.label}...`}
          readOnly={false}
        />

        {/* Question Auto-generate button */}
        {activeQuestionTab === 'plural' && (
          <div className="mt-4">
            <button
              type="button"
              className="btn btn-outline-dark"
              onClick={handleQuestionAutoGenerate}
              disabled={isGenerating || !questionVariations.singular}
              title={!questionVariations.singular ? 'Ingresa primero el texto singular' : 'Generar texto plural automáticamente basado en el singular'}
            >
              {isGenerating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Generando...
                </>
              ) : (
                <>
                  <i className="bi bi-magic me-2"></i>
                  Autogenerar texto plural
                </>
              )}
            </button>
            {!questionVariations.singular && (
              <small className="text-warning ms-2">
                <i className="bi bi-exclamation-triangle me-1"></i>
                Ingresa el texto singular primero
              </small>
            )}
            {error && (
              <div className="alert alert-danger mt-2" role="alert">
                <i className="bi bi-exclamation-circle me-2"></i>
                {typeof error === 'string' ? error : 'Error al generar el texto'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Question Image Section */}
      <div className="mb-4">
        <label className="form-label">Imagen de la pregunta (opcional)</label>
        {!testQuestionImage ? (
          <div>
            <input
              type="file"
              id="question-image-upload"
              className="d-none"
              accept="image/*"
              onChange={handleQuestionImageUpload}
            />
            <label htmlFor="question-image-upload" className="btn btn-outline-secondary d-block text-start">
              <i className="bi bi-image me-2"></i>
              Seleccionar imagen
            </label>
          </div>
        ) : (
          <div className="position-relative d-inline-block">
            <img
              src={testQuestionImage}
              alt="Vista previa de la pregunta"
              style={{
                maxWidth: '200px',
                maxHeight: '150px',
                borderRadius: '4px',
                border: '1px solid #ddd'
              }}
            />
            <button
              type="button"
              className="btn btn-sm btn-danger position-absolute top-0 end-0"
              onClick={removeQuestionImage}
              title="Eliminar imagen"
              style={{ transform: 'translate(50%, -50%)' }}
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}
      </div>

      {/* Multiple Answers Toggle */}
      <div className="mb-4">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="allowMultipleAnswers"
            checked={allowMultipleAnswers}
            onChange={(e) => onAllowMultipleChange(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="allowMultipleAnswers" style={{ color: '#000' }}>
            Permitir seleccionar múltiples respuestas
          </label>
          <div className="form-text">
            {allowMultipleAnswers
              ? 'El usuario podrá seleccionar múltiples respuestas'
              : 'El usuario solo podrá seleccionar una respuesta'
            }
          </div>
        </div>
      </div>

      {/* Answers Section */}
      <div className="mb-4">
        <label className="form-label">Respuestas</label>
        <small className="text-muted d-block mb-3">
          Configura las respuestas en singular y plural.
        </small>

        {/* Answers list - visible only when not editing */}
        {!isAddingNewAnswer && editingAnswerIndex === null && (
          <div className="mb-4">
            {answers.length === 0 ? (
              <div className="text-center text-muted py-5 border rounded">
                <i className="bi bi-list-check fs-1 d-block mb-2"></i>
                No hay respuestas agregadas
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {answers.map((answer, index) => (
                  <div
                    key={answer.id}
                    className="border rounded p-3 bg-white shadow-sm"
                    style={{
                      borderColor: '#dee2e6',
                      transition: 'box-shadow 0.2s',
                      cursor: 'default'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1 me-3">
                        <div className="d-flex align-items-center mb-2 gap-2">
                          <span className="badge bg-secondary">Respuesta {index + 1}</span>
                          {answer.isFreeText && (
                            <span className="badge bg-info">Respuesta con texto</span>
                          )}
                        </div>
                        <div
                          className="text-muted"
                          dangerouslySetInnerHTML={{ __html: answer.text.singular || '<em>Sin texto singular</em>' }}
                          style={{
                            wordBreak: 'break-word',
                            fontSize: '0.95rem',
                            lineHeight: '1.5'
                          }}
                        />
                        {answer.image && (
                          <div className="mt-2">
                            <img
                              src={answer.image}
                              alt="Imagen de respuesta"
                              style={{
                                maxWidth: '100px',
                                maxHeight: '60px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="d-flex gap-2 flex-shrink-0">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-dark"
                          onClick={() => startEditingAnswer(index)}
                          title="Editar"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-dark"
                          onClick={() => deleteAnswer(index)}
                          title="Eliminar"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add new button - visible when not editing */}
        {!isAddingNewAnswer && editingAnswerIndex === null && (
          <div className="text-center">
            <button
              type="button"
              className="btn btn-outline-dark"
              onClick={startAddingAnswer}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Añadir nueva respuesta
            </button>
          </div>
        )}

        {/* Answer Editor with tabs - visible when editing or adding */}
        {(isAddingNewAnswer || editingAnswerIndex !== null) && (
          <div className="mt-4">
            <h6 className="mb-3">
              {isAddingNewAnswer ? 'Añadir nueva respuesta' : `Editar respuesta ${editingAnswerIndex! + 1}`}
            </h6>

            {/* Answer Tab Navigation */}
            <ul className="nav nav-tabs mb-0">
              {tabs.map(tab => (
                <li key={tab.key} className="nav-item">
                  <button
                    className={`nav-link ${activeAnswerTab === tab.key ? 'active' : ''}`}
                    onClick={() => loadAnswerVariationText(tab.key)}
                    type="button"
                    style={{ color: '#000' }}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* Answer Editor */}
            <div className="mt-3">
              <DraftEditor
                ref={answerEditorRef}
                defaultValue={newAnswerText}
                onChange={(html) => setNewAnswerText(html)}
                placeholder={`Escribe el texto de la respuesta para ${tabs.find(t => t.key === activeAnswerTab)?.label}...`}
                readOnly={false}
              />

              {/* Auto-generate button */}
              {activeAnswerTab === 'plural' && !isAddingNewAnswer && editingAnswerIndex !== null && (
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-dark"
                    onClick={handleAnswerAutoGenerate}
                    disabled={isGenerating || !answers[editingAnswerIndex]?.text.singular}
                    title={!answers[editingAnswerIndex]?.text.singular ? 'Ingresa primero el texto singular' : 'Generar texto plural automáticamente basado en el singular'}
                  >
                    {isGenerating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Generando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-magic me-2"></i>
                        Autogenerar texto plural
                      </>
                    )}
                  </button>
                  {!answers[editingAnswerIndex]?.text.singular && (
                    <small className="text-warning ms-2">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Esta respuesta necesita texto singular para autogenerar
                    </small>
                  )}
                </div>
              )}

              {/* Answer options */}
              <div className="mt-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="answerIsFreeText"
                    checked={newAnswerIsFreeText}
                    onChange={(e) => setNewAnswerIsFreeText(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="answerIsFreeText" style={{ color: '#000' }}>
                    Permitir responder con texto
                  </label>
                </div>
              </div>

              {/* Answer image */}
              <div className="mt-3">
                <label className="form-label">Imagen de la respuesta (opcional)</label>
                {!newAnswerImage ? (
                  <div>
                    <input
                      type="file"
                      id="answer-image-upload"
                      className="d-none"
                      accept="image/*"
                      onChange={handleAnswerImageUpload}
                    />
                    <label htmlFor="answer-image-upload" className="btn btn-outline-secondary btn-sm">
                      <i className="bi bi-image me-2"></i>
                      Seleccionar imagen
                    </label>
                  </div>
                ) : (
                  <div className="position-relative d-inline-block">
                    <img
                      src={newAnswerImage}
                      alt="Vista previa de la respuesta"
                      style={{
                        maxWidth: '150px',
                        maxHeight: '100px',
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0"
                      onClick={removeAnswerImage}
                      title="Eliminar imagen"
                      style={{ transform: 'translate(50%, -50%)' }}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={cancelAnswerEdit}
                >
                  <i className="bi bi-x me-1"></i>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={saveAnswer}
                  disabled={!newAnswerText.trim()}
                >
                  <i className="bi bi-check me-1"></i>
                  Guardar
                </button>
              </div>

              {/* Error display */}
              {error && (
                <div className="alert alert-danger mt-3" role="alert">
                  <i className="bi bi-exclamation-circle me-2"></i>
                  {typeof error === 'string' ? error : 'Error al generar el texto'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Editable Toggle - Moved to the end */}
      {showEditableToggle && (
        <div className="mt-4">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="questionMultipleEditable"
              checked={isEditable}
              onChange={(e) => onEditableChange(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="questionMultipleEditable" style={{ color: '#000' }}>
              Permitir edición por Arquitectos
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionMultipleEditor;
